import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useEditRequirementQuestionMutation } from 'queries/requirements'
import { useRequirementDetailContextValue } from '../../../context/RequirementDetailProvider'
import { useRequirementDetailWorkflowsContextValue } from '../../../context/RequirementDetailWorkflowProvider'

type EditQuestionFlowContextState = {
  state: {
    question: NonNullable<
      ReturnType<
        typeof useRequirementDetailWorkflowsContextValue
      >['editQuestionFlow']['data']
    >['question']
    mutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    submit: ReturnType<typeof useEditRequirementQuestionMutation>['mutate']
    reset: ReturnType<typeof useEditRequirementQuestionMutation>['reset']
  }
}

const [EditQuestionFlowContext, useEditQuestionFlowContextValue] =
  createContext<EditQuestionFlowContextState>({
    name: 'EditQuestionFlowContext',
  })

export const EditQuestionFlowProvider = ({ children }: PropsWithChildren) => {
  const {
    state: { id: requirementId },
  } = useRequirementDetailContextValue()

  const {
    editQuestionFlow: { isOpen, data: flowData },
  } = useRequirementDetailWorkflowsContextValue()

  const { mutate, reset, isPending, isError, error } =
    useEditRequirementQuestionMutation(requirementId)

  if (isOpen && !flowData) {
    throw new Error(
      'Unable to find edit question flow data. Please report this error to the engineering team.'
    )
  }

  if (!flowData) {
    return null
  }

  const value: EditQuestionFlowContextState = {
    state: {
      question: flowData.question,
      mutationState: {
        isPending,
        isError,
        errorMessage: parseErrorMessage(error),
      },
    },
    actions: {
      submit: mutate,
      reset,
    },
  }

  return (
    <EditQuestionFlowContext.Provider value={value}>
      {children}
    </EditQuestionFlowContext.Provider>
  )
}

export { useEditQuestionFlowContextValue }
